import { useState } from 'react';
import { theme } from '../theme';

// New (Sep 20) — the reactivate_pin behavior change: a departure_passed
// pin can no longer reactivate with a single click, since that left it
// active but still pointing at the same past flight it was deactivated
// for (see reactivate_pin's own docstring in routes/trips.py for the
// full chain this closes, traced through the Glasgow freeze bug).
// Deliberately a small, purpose-built modal rather than routing through
// the full PinTrip.js edit form — edit_pin already explicitly refuses
// to edit an inactive pin at all ("Pin it again to start tracking a new
// date"), so reusing that screen isn't actually available here; this
// asks for exactly the one thing reactivation now requires and nothing
// else. round_trip decides whether a return-date field shows at all —
// a pin that was one-way stays one-way on reactivation, matching its
// original shape rather than asking for something it never had.
export default function ReactivateDateModal({ pin, onClose, onSubmit, submitting, error }) {
  const today = new Date().toISOString().split('T')[0];
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const roundTrip = !!pin?.return_date;

  if (!pin) return null;

  const handleSubmit = () => {
    onSubmit({
      departure_date: departureDate,
      return_date: roundTrip ? returnDate : null
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px', padding: '28px 24px',
          maxWidth: '360px', width: '100%'
        }}
      >
        <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>📅</div>
        <div style={{ fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '17px', color: theme.textPrimary, marginBottom: '10px', textAlign: 'center' }}>
          Set a new departure date
        </div>
        <div style={{ fontSize: '13.5px', color: theme.textMuted, lineHeight: 1.5, marginBottom: '18px', textAlign: 'center' }}>
          This trip's original date already passed. Pick a real date to start tracking it again.
        </div>

        <label style={{ display: 'block', fontSize: '12px', color: theme.textMuted, marginBottom: '4px', fontWeight: 700 }}>
          Departure date
        </label>
        <input
          type="date"
          value={departureDate}
          min={today}
          onChange={(e) => setDepartureDate(e.target.value)}
          style={{
            width: '100%', padding: '10px', marginBottom: roundTrip ? '14px' : '18px', border: `2px solid ${theme.border}`,
            borderRadius: '0px', fontSize: '14px', fontFamily: theme.fontDisplay, boxSizing: 'border-box'
          }}
        />

        {roundTrip && (
          <>
            <label style={{ display: 'block', fontSize: '12px', color: theme.textMuted, marginBottom: '4px', fontWeight: 700 }}>
              Return date
            </label>
            <input
              type="date"
              value={returnDate}
              min={departureDate || today}
              onChange={(e) => setReturnDate(e.target.value)}
              style={{
                width: '100%', padding: '10px', marginBottom: '18px', border: `2px solid ${theme.border}`,
                borderRadius: '0px', fontSize: '14px', fontFamily: theme.fontDisplay, boxSizing: 'border-box'
              }}
            />
          </>
        )}

        {error && (
          <div style={{ fontSize: '12.5px', color: theme.danger, marginBottom: '14px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleSubmit}
            disabled={submitting || !departureDate || (roundTrip && !returnDate)}
            style={{
              flex: 1,
              background: submitting ? theme.inactiveGray : theme.action,
              color: submitting ? theme.textMuted : '#1a1a1a',
              border: 'none', fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '13.5px',
              padding: '12px', borderRadius: '0px', cursor: submitting ? 'default' : 'pointer'
            }}
          >
            {submitting ? 'Reactivating...' : 'Start tracking'}
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1, background: '#fff', color: theme.textPrimary, border: `2px solid ${theme.border}`,
              fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '13.5px',
              padding: '12px', borderRadius: '0px', cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}